import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { DiagramBlock } from '@/components/DiagramBlock';
import { formatTime, TRACK_LABELS } from '@/lib/tracks';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { CheckCircle2, XCircle, Circle, Target, TrendingUp, Lightbulb, Sparkles, RotateCcw, Home } from 'lucide-react';

export default function ResultsPage() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/test/${sessionId}`);
        if (data.status !== 'submitted') {
          nav(`/test/${sessionId}`);
          return;
        }
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Report not found');
        nav('/dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId, nav]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Correct', value: data.correct || 0, color: '#15803D' },
      { name: 'Wrong', value: data.wrong || 0, color: '#DC2626' },
      { name: 'Unattempted', value: data.unattempted || 0, color: '#94A3B8' },
    ];
  }, [data]);

  const retake = async () => {
    if (!data) return;
    try {
      const { data: s } = await api.post('/test/start', {
        track: data.track,
        klass: data.klass,
        subject: data.subject,
        chapter: data.chapter,
        set_number: data.set_number === 1 ? 2 : 1,
      });
      nav(`/test/${s.session_id}`, { state: { session: s } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start retake');
    }
  };

  if (loading || !data) {
    return (
      <AppShell>
        <div className="grid min-h-[50vh] place-items-center text-muted-foreground">Loading your report…</div>
      </AppShell>
    );
  }

  const report = data.report_card || {};
  const stats = report.stats || {
    total: data.total,
    correct: data.correct,
    wrong: data.wrong,
    unattempted: data.unattempted,
    percentage: data.percentage,
    time_taken: data.time_taken_seconds,
    duration: data.duration_seconds,
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="report-page">
        {/* Hero */}
        <section className="report-wash noise-bg rounded-2xl border bg-card p-5 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="secondary">{TRACK_LABELS[data.track] || data.track}</Badge>
            <Badge variant="secondary">Class {data.klass}</Badge>
            <Badge variant="secondary">{data.subject}</Badge>
            <Badge variant="secondary">Set {data.set_number}</Badge>
          </div>
          <h1 className="mt-2 font-display text-2xl font-semibold sm:text-3xl">{data.chapter}</h1>
          <div className="mt-4 grid gap-4 sm:grid-cols-4" data-testid="report-score">
            <div className="rounded-xl border bg-card p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Score</div>
              <div className="font-display text-3xl font-semibold">
                {stats.correct}/{stats.total}
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Percentage</div>
              <div className="font-display text-3xl font-semibold">{stats.percentage}%</div>
              <Progress value={stats.percentage} className="mt-2" />
            </div>
            <div className="rounded-xl border bg-card p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Grade</div>
              <div className="font-display text-3xl font-semibold">{report.grade || '—'}</div>
            </div>
            <div className="rounded-xl border bg-card p-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Time taken</div>
              <div className="font-display text-3xl font-semibold">{formatTime(stats.time_taken || 0)}</div>
              <div className="text-[11px] text-muted-foreground">Your stopwatch reading</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button className="gap-1.5" onClick={retake} data-testid="retake-test-button">
              <RotateCcw className="h-4 w-4" />
              Retake other set
            </Button>
            <Button variant="outline" onClick={() => nav('/history')} data-testid="go-to-history-button">
              View history
            </Button>
            <Button variant="ghost" onClick={() => nav('/dashboard')} className="gap-1.5" data-testid="go-home-button">
              <Home className="h-4 w-4" />
              Home
            </Button>
          </div>
        </section>

        {/* AI Report Bento */}
        <section className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardContent className="space-y-3 p-5 sm:p-6">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <div className="font-display text-lg font-semibold">AI Summary</div>
              </div>
              <p className="text-sm text-muted-foreground sm:text-base">{report.summary || 'Report unavailable.'}</p>
              {report.motivational_message && (
                <div className="rounded-lg border bg-secondary/50 p-3 text-sm">
                  <strong className="mr-1">Coach note:</strong>
                  {report.motivational_message}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="mb-2 font-display text-sm font-semibold text-muted-foreground">Breakdown</div>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData} innerRadius={45} outerRadius={80} paddingAngle={2} dataKey="value">
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1 text-xs">
                {chartData.map((c) => (
                  <div key={c.name} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded-sm" style={{ background: c.color }} />
                      {c.name}
                    </span>
                    <span className="font-mono-ui">{c.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="report-weak-topics">
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-[hsl(var(--danger))]" />
                <div className="font-display text-lg font-semibold">Weak topics</div>
              </div>
              {report.weak_topics && report.weak_topics.length ? (
                <ul className="space-y-2">
                  {report.weak_topics.map((w, i) => (
                    <li key={i} className="rounded-lg border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium">{w.topic}</div>
                        <Badge
                          variant={w.priority === 'high' ? 'destructive' : 'secondary'}
                          className="capitalize"
                        >
                          {w.priority || 'medium'}
                        </Badge>
                      </div>
                      {w.reason && <div className="mt-1 text-xs text-muted-foreground">{w.reason}</div>}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground">No specific weak areas detected — nice!</div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="report-mistake-patterns">
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[hsl(var(--warning))]" />
                <div className="font-display text-lg font-semibold">Mistake patterns</div>
              </div>
              {report.mistake_patterns && report.mistake_patterns.length ? (
                <ul className="space-y-2 text-sm">
                  {report.mistake_patterns.map((m, i) => (
                    <li key={i} className="rounded-lg border p-3">{m}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground">No repeating patterns detected.</div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-3" data-testid="report-recommendations">
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-[hsl(var(--accent))]" />
                <div className="font-display text-lg font-semibold">Recommendations</div>
              </div>
              {report.recommendations && report.recommendations.length ? (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {report.recommendations.map((r, i) => (
                    <div key={i} className="rounded-lg border p-3">
                      <div className="font-medium">{r.action}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{r.resource}</div>
                      {r.estimated_time_minutes && (
                        <Badge variant="outline" className="mt-2">
                          ~{r.estimated_time_minutes} min
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No specific recommendations — keep it up!</div>
              )}
              {report.next_steps && report.next_steps.length ? (
                <div className="pt-2">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Next steps</div>
                  <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                    {report.next_steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </section>

        {/* Question review */}
        <section>
          <Tabs defaultValue="review">
            <TabsList>
              <TabsTrigger value="review" data-testid="tab-review">
                Question review
              </TabsTrigger>
              <TabsTrigger value="strengths" data-testid="tab-strengths">
                Strengths
              </TabsTrigger>
            </TabsList>

            <TabsContent value="review" className="mt-4">
              <Card>
                <CardContent className="p-5" data-testid="report-question-review">
                  <Accordion type="single" collapsible className="space-y-2">
                    {(data.questions || []).map((q, idx) => {
                      const userChoice = data.answers?.[String(q.id)];
                      const isCorrect = userChoice !== undefined && userChoice === q.correct_index;
                      const isUn = userChoice === undefined || userChoice === null;
                      return (
                        <AccordionItem key={q.id} value={`q-${q.id}`} className="rounded-lg border">
                          <AccordionTrigger className="px-4 hover:no-underline" data-testid={`review-question-${idx + 1}`}>
                            <div className="flex w-full items-center gap-3">
                              {isUn ? (
                                <Circle className="h-5 w-5 text-muted-foreground" />
                              ) : isCorrect ? (
                                <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" />
                              ) : (
                                <XCircle className="h-5 w-5 text-[hsl(var(--danger))]" />
                              )}
                              <span className="text-left text-sm">
                                <strong className="mr-2">Q{idx + 1}.</strong>
                                <span className="text-muted-foreground">{q.stem?.slice(0, 90)}{q.stem?.length > 90 ? '…' : ''}</span>
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-3">
                              <div className="text-sm sm:text-base">{q.stem}</div>
                              <DiagramBlock diagram={q.diagram} />
                              <ul className="space-y-1.5">
                                {q.options?.map((opt, i) => {
                                  const isThisCorrect = i === q.correct_index;
                                  const isYours = i === userChoice;
                                  return (
                                    <li
                                      key={i}
                                      className={`rounded-md border p-2 text-sm ${
                                        isThisCorrect
                                          ? 'border-[hsl(var(--success)/0.6)] bg-[hsl(var(--success)/0.08)]'
                                          : isYours
                                          ? 'border-[hsl(var(--danger)/0.6)] bg-[hsl(var(--danger)/0.06)]'
                                          : ''
                                      }`}
                                    >
                                      <strong className="mr-2">{'ABCD'[i]}.</strong>
                                      {opt}
                                      {isThisCorrect && <Badge variant="secondary" className="ml-2">Correct</Badge>}
                                      {isYours && !isThisCorrect && (
                                        <Badge variant="destructive" className="ml-2">Your answer</Badge>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                              {q.explanation && (
                                <div className="rounded-md border bg-secondary/40 p-3 text-sm">
                                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Explanation</div>
                                  {q.explanation}
                                </div>
                              )}
                              {q.topic_tag && <Badge variant="outline">{q.topic_tag}</Badge>}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="strengths" className="mt-4">
              <Card>
                <CardContent className="p-5">
                  <div className="mb-2 text-sm text-muted-foreground">Topics you did well on:</div>
                  {report.strengths && report.strengths.length ? (
                    <div className="flex flex-wrap gap-2">
                      {report.strengths.map((s, i) => (
                        <Badge key={i} variant="secondary" className="text-sm">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">Attempt more questions to build a strengths profile.</div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </AppShell>
  );
}
